import React, { useEffect, useRef, useState } from 'react';
import * as faceapi from 'face-api.js';
import api from '../config/api';

export default function FaceVerification({ isOpen, onSuccess, onCancel }) {
  const videoRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [registered, setRegistered] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [modelsLoaded, setModelsLoaded] = useState(false);

  const fetchStatus = async () => {
    try {
      const res = await api.get('/api/face', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
       // sudah /api dari baseURL
      setRegistered(res.data.registered);
    } catch (err) {
      console.error('Gagal memuat status wajah:', err);
    } finally {
      setLoading(false);
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user' } 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      console.log('✅ Camera started successfully');
    } catch (err) {
      console.error('❌ Error accessing camera:', err);
      alert('Gagal mengakses kamera. Pastikan Anda memberikan izin akses kamera.');
    }
  };

  const loadModels = async () => {
    try {
      // Gunakan CDN sebagai fallback jika local models bermasalah
      const USE_CDN = true; // Set ke false jika ingin pakai local models
      
      const MODEL_URL = USE_CDN 
        ? 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api/model'
        : window.location.origin + '/models';
      
      console.log('🔄 Loading face-api models from:', MODEL_URL);
      
      if (!USE_CDN) {
        // Test apakah URL dapat diakses (hanya untuk local)
        const testUrl = `${MODEL_URL}/tiny_face_detector/tiny_face_detector_model-weights_manifest.json`;
        console.log('Testing model URL:', testUrl);
        
        const testResponse = await fetch(testUrl);
        if (!testResponse.ok) {
          throw new Error(`Cannot access models: ${testResponse.status} ${testResponse.statusText}`);
        }
        console.log('✅ Model files are accessible');
      }
      
      await faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
      console.log('✅ Tiny face detector loaded');
      
      await faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL);
      console.log('✅ Face landmark loaded');
      
      await faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL);
      console.log('✅ Face recognition loaded');
      
      setModelsLoaded(true);
      console.log('✅ All face-api models loaded successfully');
    } catch (err) {
      console.error('❌ Error loading face-api models:', err);
      console.error('Error details:', {
        message: err.message,
        stack: err.stack
      });
      
      // Pesan error yang lebih jelas
      let errorMsg = 'Gagal memuat model wajah. ';
      if (err.message.includes('JSON')) {
        errorMsg += 'Model files tidak ditemukan atau tidak dapat diakses.';
      } else if (err.message.includes('tensor')) {
        errorMsg += 'Model files corrupt atau tidak kompatibel. Gunakan CDN atau download ulang models.';
      } else {
        errorMsg += err.message;
      }
      
      alert(errorMsg);
      setModelsLoaded(false);
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject;
      const tracks = stream.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
  };

  const captureImage = () => {
    const canvas = document.createElement('canvas');
    const scale = 0.5; // resize jadi 50% biar lebih kecil
    canvas.width = videoRef.current.videoWidth * scale;
    canvas.height = videoRef.current.videoHeight * scale;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.7); // kualitas 70%
  };
  

  const handleRegister = async () => {
    if (!modelsLoaded) {
      alert('Model wajah belum dimuat');
      return;
    }

    setCapturing(true);
    
    try {
      const imageData = captureImage();

      // Detect faces in the captured image dengan threshold lebih rendah
      const img = new Image();
      img.src = imageData;
      await new Promise(resolve => img.onload = resolve);

      console.log('🔍 Detecting faces in image...');
      
      // Turunkan threshold untuk deteksi lebih sensitif
      const options = new faceapi.TinyFaceDetectorOptions({
        inputSize: 416,        // Ukuran input lebih besar untuk akurasi lebih baik
        scoreThreshold: 0.3    // Threshold lebih rendah (default 0.5)
      });
      
      const detections = await faceapi.detectAllFaces(img, options);
      
      console.log('📊 Detections found:', detections.length);
      console.log('Detection details:', detections);

      if (detections.length === 0) {
        alert('❌ Tidak ada wajah terdeteksi. Tips:\n• Pastikan pencahayaan cukup\n• Wajah menghadap kamera\n• Jarak tidak terlalu dekat/jauh\n• Coba lagi');
        setCapturing(false);
        return;
      }

      if (detections.length > 1) {
        alert('⚠️ Terlalu banyak wajah terdeteksi (' + detections.length + '). Pastikan hanya wajah Anda yang terlihat.');
        setCapturing(false);
        return;
      }

      console.log('✅ Single face detected, confidence:', detections[0].score);

      // Simpan ke backend
      await api.post('/api/face/register', { image: imageData });
      alert('✅ Wajah berhasil disimpan!');
      await fetchStatus();
      
      if (onSuccess) {
        stopCamera();
        onSuccess();
      }
    } catch (err) {
      console.error('❌ Error in handleRegister:', err);
      alert('Gagal menyimpan wajah: ' + (err.response?.data?.message || err.message));
    } finally {
      setCapturing(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      const init = async () => {
        try {
          console.log('🚀 Initializing face verification...');
          
          await fetchStatus();
          console.log('✅ Face status fetched');
          
          await loadModels();
          console.log('✅ Models loaded');
          
          await startCamera();
          console.log('✅ Camera started');
          
        } catch (err) {
          console.error('❌ Initialization error:', err);
        } finally {
          setLoading(false);
        }
      };
      
      init();
    } else {
      // Stop camera when modal closed
      console.log('🛑 Modal closed, stopping camera');
      stopCamera();
      setLoading(true); // Reset loading state for next open
    }

    // Cleanup saat component unmount
    return () => {
      console.log('🧹 Cleaning up face verification component');
      stopCamera();
    };
  }, [isOpen]);

  if (loading) {
    return (
      <div style={{ color: '#fff', textAlign: 'center', padding: '20px' }}>
        <p>⏳ Memuat kamera dan model wajah...</p>
      </div>
    );
  }

  if (!modelsLoaded) {
    return (
      <div style={{ color: '#ef4444', textAlign: 'center', padding: '20px' }}>
        <p>❌ Gagal memuat model wajah</p>
        {onCancel && (
          <button
            onClick={() => {
              stopCamera();
              onCancel();
            }}
            style={{
              marginTop: '1rem',
              padding: '0.5rem 1rem',
              background: '#6b7280',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Tutup
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ color: '#fff' }}>
      {registered ? (
        <p style={{ color: '#22c55e', marginBottom: '12px' }}>✅ Wajah sudah terverifikasi</p>
      ) : (
        <p style={{ color: '#fbbf24', marginBottom: '12px' }}>❌ Wajah belum terdaftar</p>
      )}

      <div style={{ position: 'relative' }}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          style={{ width: '100%', borderRadius: '8px', marginBottom: '12px', background: '#000' }}
        />
        
        {/* Overlay guide untuk posisi wajah */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '60%',
          height: '70%',
          border: '2px dashed rgba(255, 255, 255, 0.5)',
          borderRadius: '50%',
          pointerEvents: 'none'
        }} />
      </div>

      <div style={{ 
        background: 'rgba(59, 130, 246, 0.1)', 
        padding: '8px 12px', 
        borderRadius: '6px', 
        marginBottom: '12px',
        fontSize: '12px',
        color: 'rgba(255, 255, 255, 0.8)'
      }}>
        💡 Tips: Pastikan wajah Anda di dalam lingkaran dan pencahayaan cukup
      </div>

      <div style={{ display: 'flex', gap: '12px' }}>
        <button
          onClick={handleRegister}
          disabled={capturing || !modelsLoaded}
          style={{
            flex: 1,
            padding: '10px 16px',
            background: capturing || !modelsLoaded ? '#6b7280' : '#22c55e',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: capturing || !modelsLoaded ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          {capturing ? 'Memproses...' : registered ? 'Update Wajah' : 'Daftarkan Wajah'}
        </button>
        
        {onCancel && (
          <button
            onClick={() => {
              stopCamera();
              onCancel();
            }}
            disabled={capturing}
            style={{
              flex: 1,
              padding: '10px 16px',
              background: capturing ? '#6b7280' : '#ef4444',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: capturing ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Batal
          </button>
        )}
      </div>
    </div>
  );
}
